/**
 * Converts Contentful-style rich text JSON to HTML
 *
 * This is a lightweight replacement for @contentful/rich-text-html-renderer
 * that handles only the node types we actually use from our YAML transforms.
 *
 * Supported node types:
 * - document
 * - paragraph
 * - heading-1 through heading-6
 * - hyperlink
 * - unordered-list, ordered-list
 * - list-item
 * - text (with marks: bold, italic, underline, code)
 */

/**
 * Render a text node with its marks (bold, italic, etc.)
 */
function renderTextNode(node) {
  let text = node.value || ""

  // Escape HTML entities
  text = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\n/g, "<br>")

  // Apply marks in reverse order so they nest correctly
  const marks = node.marks || []
  marks.forEach((mark) => {
    switch (mark.type) {
      case "bold":
        text = `<strong>${text}</strong>`
        break
      case "italic":
        text = `<em>${text}</em>`
        break
      case "underline":
        text = `<u>${text}</u>`
        break
      case "code":
        text = `<code>${text}</code>`
        break
      default:
        break
    }
  })

  return text
}

/**
 * Recursively render a rich text node to HTML
 */
function renderNode(node) {
  if (!node) return ""

  const { nodeType } = node

  // Text node
  if (nodeType === "text") {
    return renderTextNode(node)
  }

  // Render children
  const children = (node.content || []).map(renderNode).join("")

  switch (nodeType) {
    case "document":
      return children

    case "paragraph":
      return `<p>${children}</p>`

    case "heading-1":
      return `<h1>${children}</h1>`
    case "heading-2":
      return `<h2>${children}</h2>`
    case "heading-3":
      return `<h3>${children}</h3>`
    case "heading-4":
      return `<h4>${children}</h4>`
    case "heading-5":
      return `<h5>${children}</h5>`
    case "heading-6":
      return `<h6>${children}</h6>`

    case "hyperlink": {
      const uri = node.data?.uri || "#"
      return `<a href="${uri}">${children}</a>`
    }

    case "unordered-list":
      return `<ul>${children}</ul>`

    case "ordered-list":
      return `<ol>${children}</ol>`

    case "list-item":
      return `<li>${children}</li>`

    case "blockquote":
      return `<blockquote>${children}</blockquote>`

    case "hr":
      return "<hr />"

    default:
      // For unknown node types, just render children
      return children
  }
}

/**
 * Convert Contentful-style rich text document to HTML string
 *
 * @param {Object} document - Rich text document object
 * @returns {string} HTML string
 */
export function richTextToHtml(document) {
  if (!document) return ""
  return renderNode(document)
}
